/**
 * Meta (personal_goal) detail — mostra orizzonte, progress % aggregato,
 * lista delle Azioni collegate (mete con meta_id = this.id).
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, Trash2, Zap, Plus, Award, Ban, RotateCcw, Edit3,
} from "lucide-react";
import { api } from "../lib/api";
import Tip from "../components/Tip";
import CreateAzioneModal from "../components/CreateAzioneModal";
import MetaAchieveModal from "../components/MetaAchieveModal";
import MetaCancelModal from "../components/MetaCancelModal";
import MetaEditModal from "../components/MetaEditModal";
import { celebrate } from "../lib/confetti";

const HORIZON_LABEL = { "1_year": "1 anno", "5_years": "5 anni", "10_years": "10 anni" };
const HORIZON_COLOR = { "1_year": "#8FBF8F", "5_years": "#F5C24A", "10_years": "#C3DAF4" };

export default function MetaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [goal, setGoal] = useState(null);
  const [progress, setProgress] = useState(0);
  const [azioni, setAzioni] = useState([]);
  const [meteTree, setMeteTree] = useState({ annual: [], monthly: [], weekly: [] });
  const [showCreate, setShowCreate] = useState(false);
  const [showAchieve, setShowAchieve] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const load = async () => {
    try {
      const [g, p, a, t] = await Promise.all([
        api.getGoal(id),
        api.getGoalProgress(id),
        api.listGoalAzioni(id),
        api.getMeteTree().catch(() => ({ annual: [], monthly: [], weekly: [] })),
      ]);
      setGoal(g); setProgress(p.percent_complete || 0); setAzioni(a || []);
      setMeteTree(t || { annual: [], monthly: [], weekly: [] });
    } catch { toast.error("Errore caricamento meta"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!goal) return <div className="opacity-60 text-sm py-10 text-center" data-testid="meta-detail-loading">Caricamento…</div>;

  const pct = Number(progress || 0);
  const barColor = pct >= 100 ? "#3F8F5C" : pct >= 60 ? "#8FBF8F" : pct >= 30 ? "#F5C24A" : "#95BAEF";
  const accent = HORIZON_COLOR[goal.horizon];

  const del = async () => {
    if (!window.confirm("Eliminare definitivamente questa meta? Le azioni collegate perderanno il collegamento.")) return;
    try { await api.deleteGoal(id); toast.success("Meta eliminata"); nav("/mete"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const doRestore = async () => {
    try { await api.restoreGoal(id); toast.success("Meta riportata attiva"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5" data-testid="meta-detail-page">
      <div className="pxos-card p-5" style={{ borderLeft: `4px solid ${accent}` }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="pxos-badge text-[10px] uppercase tracking-widest" style={{ background: accent }}>
              {HORIZON_LABEL[goal.horizon]}
            </div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold inline-flex items-center gap-2">
              <Target size={22}/> {goal.title}
            </h1>
            {goal.category && <div className="text-sm opacity-70 mt-1">{goal.category}</div>}
            {goal.description && (
              <p className="mt-2 text-sm opacity-80 whitespace-pre-wrap">{goal.description}</p>
            )}
            {goal.target_date && (
              <div className="mt-2 text-xs opacity-70">
                🎯 Target: <b>{new Date(goal.target_date).toLocaleDateString("it-IT")}</b>
              </div>
            )}
            {goal.status === "achieved" && (
              <div className="mt-2 pxos-badge inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }}>
                <Award size={11}/> Raggiunta{goal.achieved_at && ` · ${new Date(goal.achieved_at).toLocaleDateString("it-IT")}`}
              </div>
            )}
            {goal.status === "cancelled" && (
              <div className="mt-2 pxos-badge inline-flex items-center gap-1" style={{ background: "#F3F4F6", color: "#4B5563" }}>
                <Ban size={11}/> Annullata{goal.cancelled_at && ` · ${new Date(goal.cancelled_at).toLocaleDateString("it-IT")}`}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            {/* Lifecycle actions — Blocco 4 · F */}
            {goal.status === "active" && (
              <>
                <button onClick={() => setShowAchieve(true)}
                        className="rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1"
                        style={{ background: "#DCFCE7", color: "#166534" }}
                        data-testid="meta-detail-achieve">
                  <Award size={12}/> Dichiarare raggiunta
                </button>
                <button onClick={() => setShowCancel(true)}
                        className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1 opacity-80"
                        data-testid="meta-detail-cancel">
                  <Ban size={12}/> Annulla meta
                </button>
              </>
            )}
            {goal.status !== "active" && (
              <button onClick={doRestore}
                      className="rounded-full border px-3 py-1.5 text-xs inline-flex items-center gap-1"
                      data-testid="meta-detail-restore">
                <RotateCcw size={12}/> Ripristina come attiva
              </button>
            )}
            <div className="flex gap-1">
              <Tip label="Modifica meta">
                <button onClick={() => setShowEdit(true)}
                        className="rounded-full border px-2.5 py-1.5 text-xs inline-flex items-center gap-1"
                        data-testid="meta-detail-edit">
                  <Edit3 size={12}/> Modifica
                </button>
              </Tip>
              {goal.status === "active" && (
                <Tip label="Elimina definitivamente (solo entro 10 min dalla creazione)">
                  <button onClick={del} className="rounded-full border px-2.5 py-1.5 text-xs text-red-700" data-testid="meta-delete">
                    <Trash2 size={12}/>
                  </button>
                </Tip>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs opacity-70 mb-1">
            <span className="inline-flex items-center gap-1"><Zap size={12}/> Progresso da {azioni.length} azion{azioni.length === 1 ? "e" : "i"} collegat{azioni.length === 1 ? "a" : "e"}</span>
            <span className="font-mono font-semibold" data-testid="meta-detail-progress">{pct.toFixed(1)}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-black/5">
            <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }}/>
          </div>
        </div>

        {goal.achieved_notes && (
          <div className="mt-4 pt-4 border-t text-sm" style={{ borderColor: "var(--pxos-border)" }}>
            <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Note dal traguardo</div>
            <div className="opacity-80 whitespace-pre-wrap">{goal.achieved_notes}</div>
          </div>
        )}
        {goal.cancelled_reason && (
          <div className="mt-4 pt-4 border-t text-sm" style={{ borderColor: "var(--pxos-border)" }}>
            <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Motivo annullamento</div>
            <div className="opacity-80 whitespace-pre-wrap" data-testid="meta-detail-cancel-reason">{goal.cancelled_reason}</div>
          </div>
        )}
      </div>

      {/* Azioni collegate */}
      <div className="pxos-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-semibold inline-flex items-center gap-2">
            <Zap size={16}/> Azioni collegate
            <span className="text-[10px] uppercase tracking-widest opacity-60 px-1.5 py-0.5 rounded-full border">
              {azioni.length}
            </span>
          </div>
          <button onClick={() => setShowCreate(true)}
                  className="pxos-btn-primary text-xs inline-flex items-center gap-1"
                  data-testid="meta-add-azione">
            <Plus size={12}/> Aggiungi azione collegata
          </button>
        </div>
        {azioni.length === 0 ? (
          <div className="text-sm opacity-60 py-6 text-center" data-testid="meta-azioni-empty">
            Nessuna azione collegata. Usa <b>+ Aggiungi azione collegata</b> qui sopra
            per creare subito un piano operativo per questa meta.
          </div>
        ) : (
          <ul className="space-y-2">
            {azioni.map((a) => {
              const p = Number(a.cached_progress || 0);
              return (
                <li key={a.id}>
                  <Link to={`/azioni/${a.id}`}
                        className="block border rounded-lg p-3 hover:shadow-sm"
                        style={{ borderColor: "var(--pxos-border)" }}
                        data-testid="meta-azione-row">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{a.title}</div>
                        <div className="text-[11px] opacity-60 mt-0.5">
                          {a.level === "annual" ? "Anno" : a.level === "monthly" ? "Mese" : "Settimana"}
                          {a.deadline_date && <> · scadenza {new Date(a.deadline_date + "T00:00:00").toLocaleDateString("it-IT")}</>}
                        </div>
                      </div>
                      <span className="font-mono text-xs font-semibold">{p.toFixed(0)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden bg-black/5 mt-2">
                      <div className="h-full" style={{ width: `${Math.min(100, p)}%`, background: barColor }}/>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showCreate && (
        <CreateAzioneModal
          defaultLevel="weekly"
          tree={meteTree}
          presetMetaId={id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {showAchieve && (
        <MetaAchieveModal
          entity={{ title: goal.title }}
          onClose={() => setShowAchieve(false)}
          onConfirm={async ({ achieved_date, achieved_notes }) => {
            await api.achieveGoal(id, { achieved_date, achieved_notes });
            setShowAchieve(false); load(); celebrate({ durationMs: 2000 });
            toast.success("🎉 Meta raggiunta");
          }}
        />
      )}
      {showCancel && (
        <MetaCancelModal
          entity={{ title: goal.title }}
          onClose={() => setShowCancel(false)}
          onConfirm={async ({ reason }) => {
            await api.cancelGoal(id, reason);
            setShowCancel(false); load();
            toast.success("Meta annullata");
          }}
        />
      )}
      {showEdit && (
        <MetaEditModal
          goal={goal}
          onClose={() => setShowEdit(false)}
          saveFn={(patch) => api.patchGoal(id, patch)}
          onSaved={(g) => { setGoal(g); setShowEdit(false); }}
        />
      )}
    </div>
  );
}
